/**
 * Three onboarding wizard layout variants.
 * Import and render the one you want (or A/B test between them).
 */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { SEOHead } from "../../components/seo/SEOHead.tsx";
import {
  useOnboarding, getStepsFor, TFLogo, StepCounter, ChoicesSummary,
  NICHES, VOICES, type OnboardingState,
} from "./shared.tsx";
import { StepRenderer } from "./steps.tsx";

// ── V1: Centered card ───────────────────────────────────────────────────────

export function WizardV1({ theme = "light", short = false }: { theme?: "light" | "dark"; short?: boolean }) {
  const navigate = useNavigate();
  const [state, set] = useOnboarding();
  const steps = getStepsFor(short);
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(i => Math.min(i, steps.length - 1)); }, [steps.length]);
  const next = () => setIdx(i => Math.min(i + 1, steps.length - 1));
  const back = () => setIdx(i => Math.max(i - 1, 0));
  const cur = steps[Math.min(idx, steps.length - 1)];

  return (
    <>
    <SEOHead title="TubeForge — Get Started" description="Set up your faceless YouTube channel in 3 minutes. Pick a niche, voice, and topic." path="/onboarding" />
    <div className="ob" data-theme={theme} style={{ width: "100%", minHeight: "100vh", background: "var(--bg)", position: "relative", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 32px", borderBottom: "1px solid var(--rule)" }}>
        <TFLogo />
        <StepCounter current={idx} total={steps.length} mode="dots" />
        <button className="btn ghost" style={{ padding: "6px 12px", fontSize: 13 }} onClick={() => navigate("/dashboard")}>Save & exit</button>
      </div>
      <div style={{ flex: 1, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "60px 32px" }}>
        <div key={cur.id} style={{ width: "100%", maxWidth: 720 }}>
          <StepRenderer stepId={cur.id} state={state} set={set} next={next} back={idx === 0 ? null : back} isFirst={idx === 0} isLast={idx === steps.length - 1} />
        </div>
      </div>
      <div style={{ padding: "14px 32px", borderTop: "1px solid var(--rule)", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "var(--fg-muted)" }}>
        <span className="mono">{cur.title}</span>
        <span>Need help? <a style={{ color: "var(--accent)" }}>Email support</a></span>
      </div>
    </div>
  </>
  );
}

// ── V2: Split-pane sidebar ──────────────────────────────────────────────────

export function WizardV2({ theme = "light", short = false }: { theme?: "light" | "dark"; short?: boolean }) {
  const navigate = useNavigate();
  const [state, set] = useOnboarding();
  const steps = getStepsFor(short);
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(i => Math.min(i, steps.length - 1)); }, [steps.length]);
  const next = () => setIdx(i => Math.min(i + 1, steps.length - 1));
  const back = () => setIdx(i => Math.max(i - 1, 0));
  const cur = steps[Math.min(idx, steps.length - 1)];

  return (
    <>
    <SEOHead title="TubeForge — Get Started" description="Set up your faceless YouTube channel in 3 minutes. Pick a niche, voice, and topic." path="/onboarding/v2" />
    <div className="ob" data-theme={theme} style={{ width: "100%", minHeight: "100vh", background: "var(--bg)", display: "flex" }}>
      <aside style={{ width: 280, borderRight: "1px solid var(--rule)", padding: "24px 20px", display: "flex", flexDirection: "column", gap: 24, background: "var(--bg-2)" }}>
        <TFLogo />
        <div>
          <div className="label" style={{ marginBottom: 12 }}>Setup</div>
          <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
            {steps.map((s, i) => {
              const done = i < idx;
              const active = i === idx;
              return (
                <li key={s.id}>
                  <button
                    onClick={() => done && setIdx(i)}
                    disabled={!done && !active}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", gap: 12,
                      padding: "10px 12px", borderRadius: 8,
                      border: "1px solid " + (active ? "var(--rule)" : "transparent"),
                      background: active ? "var(--bg)" : "transparent",
                      cursor: done ? "pointer" : "default",
                      font: "inherit", color: "inherit", textAlign: "left",
                      boxShadow: active ? "var(--shadow-sm)" : "none",
                    }}
                  >
                    <span style={{
                      width: 22, height: 22, borderRadius: "50%",
                      background: done ? "var(--accent)" : active ? "var(--accent-soft)" : "var(--surface)",
                      color: done ? "#fff" : active ? "var(--accent)" : "var(--fg-muted)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 600, flexShrink: 0,
                    }} className="mono">
                      {done ? (
                        <svg width="11" height="11" viewBox="0 0 11 11"><path d="M2 5.5l2.5 2.5L9 3" stroke="white" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      ) : (i + 1)}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: active ? 600 : 400, color: active || done ? "var(--fg)" : "var(--fg-muted)" }}>
                      {s.title}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
        <div style={{ marginTop: "auto", padding: 14, background: "var(--bg)", borderRadius: 10, border: "1px solid var(--rule)" }}>
          <div className="label" style={{ marginBottom: 10 }}>Your setup</div>
          <ChoicesSummary state={state} compact />
        </div>
      </aside>
      <main style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 32px", borderBottom: "1px solid var(--rule)" }}>
          <div className="label">Step {idx + 1} of {steps.length}</div>
          <button className="btn ghost" style={{ padding: "6px 12px", fontSize: 13 }} onClick={() => navigate("/dashboard")}>Save & exit</button>
        </div>
        <div style={{ flex: 1, padding: "56px 48px", overflow: "auto" }}>
          <div key={cur.id} style={{ maxWidth: 720 }}>
            <StepRenderer stepId={cur.id} state={state} set={set} next={next} back={idx === 0 ? null : back} isFirst={idx === 0} isLast={idx === steps.length - 1} />
          </div>
        </div>
      </main>
    </div>
  </>
  );
}

// ── V3: Conversational ──────────────────────────────────────────────────────

export function WizardV3({ theme = "light", short = false }: { theme?: "light" | "dark"; short?: boolean }) {
  const navigate = useNavigate();
  const [state, set] = useOnboarding();
  const steps = getStepsFor(short);
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(i => Math.min(i, steps.length - 1)); }, [steps.length]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const next = () => setIdx(i => Math.min(i + 1, steps.length - 1));
  const back = () => setIdx(i => Math.max(i - 1, 0));

  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current;
      requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
    }
  }, [idx]);

  const safeIdx = Math.min(idx, steps.length - 1);
  const visible = steps.slice(0, safeIdx + 1);

  return (
    <>
    <SEOHead title="TubeForge — Get Started" description="Set up your faceless YouTube channel in 3 minutes. Pick a niche, voice, and topic." path="/onboarding/v3" />
    <div className="ob" data-theme={theme} style={{ width: "100%", minHeight: "100vh", background: "var(--bg)", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 32px", borderBottom: "1px solid var(--rule)" }}>
        <TFLogo />
        <div style={{ flex: 1, maxWidth: 400, margin: "0 32px" }}>
          <StepCounter current={safeIdx} total={steps.length} mode="bar" />
        </div>
        <button className="btn ghost" style={{ padding: "6px 12px", fontSize: 13 }} onClick={() => navigate("/dashboard")}>Save & exit</button>
      </div>
      <div ref={scrollRef} style={{ flex: 1, overflow: "auto", padding: "32px 24px 24px" }}>
        <div style={{ maxWidth: 680, margin: "0 auto", display: "flex", flexDirection: "column", gap: 22 }}>
          {visible.map((s, i) => {
            const isCurrent = i === safeIdx;
            return (
              <React.Fragment key={s.id}>
                <Bubble side="left">
                  <Prompt stepId={s.id} />
                </Bubble>
                {isCurrent ? (
                  <Bubble side="right" interactive>
                    <StepRenderer stepId={s.id} state={state} set={set} next={next} back={i === 0 ? null : back} isFirst={i === 0} isLast={i === steps.length - 1} />
                  </Bubble>
                ) : (
                  <Bubble side="right">
                    <Answer stepId={s.id} state={state} onEdit={() => setIdx(i)} />
                  </Bubble>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  </>
  );
}

function Bubble({ side, children, interactive }: { side: "left" | "right"; children: React.ReactNode; interactive?: boolean }) {
  const isLeft = side === "left";
  return (
    <div style={{ display: "flex", justifyContent: isLeft ? "flex-start" : "flex-end" }}>
      <div style={{
        maxWidth: interactive ? "100%" : "78%",
        width: interactive ? "100%" : "auto",
        padding: interactive ? 20 : "12px 16px",
        background: isLeft ? "var(--surface)" : "var(--bg)",
        border: "1px solid " + (isLeft ? "transparent" : "var(--rule)"),
        borderRadius: 14,
        borderTopLeftRadius: isLeft ? 4 : 14,
        borderTopRightRadius: isLeft ? 14 : 4,
        fontSize: 14,
        boxShadow: interactive ? "var(--shadow-sm)" : "none",
      }}>{children}</div>
    </div>
  );
}

const PROMPTS: Record<string, string> = {
  welcome: "Hey 👋 I'm TubeForge. I'll set you up to ship your first faceless video — should take three minutes. Ready?",
  topic:   "Let's start with the topic. What's your first video about? One sentence is plenty.",
  format:  "Long-form (10–30 min) or Shorts (under 60s)?",
  niche:   "Pick a niche preset — it pre-configures your script style and image generation.",
  channel: "Now let's name your channel and set the tone of voice for scripts.",
  voice:   "Pick a narrator. Tap to preview each one.",
  youtube: "Want to publish directly to YouTube? Optional — you can also just download MP4s.",
  kickoff: "All set. Generating your first script now…",
};

function Prompt({ stepId }: { stepId: string }) {
  return <div style={{ lineHeight: 1.55 }}>{PROMPTS[stepId]}</div>;
}

function Answer({ stepId, state, onEdit }: { stepId: string; state: OnboardingState; onEdit: () => void }) {
  const answers: Record<string, string> = {
    welcome: "Let's go.",
    topic:   state.topic || "(skipped)",
    format:  state.format === "long" ? "Long-form, 16:9" : "Shorts, 9:16",
    niche:   NICHES.find(n => n.id === state.niche)?.name || "(none)",
    channel: state.channelName ? `${state.channelName} · ${state.tone}` : "(none)",
    voice:   VOICES.find(v => v.id === state.voice)?.name
      ? `${VOICES.find(v => v.id === state.voice)!.name} · ${VOICES.find(v => v.id === state.voice)!.accent}`
      : "(none)",
    youtube: state.youtubeConnected ? "Connected ✓" : "Skipped",
    kickoff: "Building.",
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}>
      <span>{answers[stepId]}</span>
      <button onClick={onEdit} style={{
        background: "none", border: "none", color: "var(--fg-muted)",
        cursor: "pointer", fontSize: 12, fontFamily: "inherit", padding: "2px 6px",
      }}>Edit</button>
    </div>
  );
}
